import * as Gsap from "gsap";
import * as HLF from "html-living-framework";
import { CaptionToken } from "../Data/CaptionToken";
import * as Topics from "../Data/Topics";
import { IOnboardingTarget } from "../GameObjects/IOnboardingTarget";

export class Button extends HLF.Button implements HLF.Input.IFocusable, IOnboardingTarget {
    protected static readonly SCALE_NORMAL: number = 1.0;
    protected static readonly SCALE_OVER: number = 1.05;
    protected static readonly SCALE_DOWN: number = 0.95;

    protected _hoverVO: string | undefined;
    protected _clickedSFX: string | undefined;
    protected _canPlayVo: boolean;
    protected _pubSubTokens: any[];

    constructor(pCallback: () => void, pAsset?: string, pSheet?: string, pHoverVO?: string, pClickedSFX?: string) {
        super(pCallback, pAsset, pSheet);
        this._hoverVO = pHoverVO;
        this._clickedSFX = pClickedSFX;
        this._canPlayVo = true;
        this._pubSubTokens = new Array<any>();

        this._pubSubTokens.push(PubSub.subscribe(Topics.FUN_FACT_START, () => { this._canPlayVo = false; }));
        this._pubSubTokens.push(PubSub.subscribe(Topics.FUN_FACT_COMPLETE, () => { this._canPlayVo = true; }));
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        delete this._callback;
        for (let i = 0; i < this._pubSubTokens.length; ++i) {
            PubSub.unsubscribe(this._pubSubTokens[i]);
        }
        super.destroy(pOptions);
    }

    public onFocusBegin(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_BEGIN, this);
        this.playHoverVO();
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this._callback();
            this.playClickedSFX();
        }
    }

    public onFocusEnd(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_END, this);
    }

    public getFocusPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getFocusSize(): PIXI.Point {
        return new PIXI.Point(this.width, this.height);
    }

    public getOnboardingPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getOnboardingRotation(): number {
        return 0;
    }

    protected onPointerOver(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerOver(pEvent);
        Gsap.TweenMax.to(this._visuals.scale, 0.15, {
            x: Button.SCALE_OVER,
            y: Button.SCALE_OVER,
            ease: Gsap.Sine.easeInOut,
        });
        this.playHoverVO();
    }

    protected onPointerDown(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerDown(pEvent);
        Gsap.TweenMax.to(this._visuals.scale, 0.15, {
            x: Button.SCALE_DOWN,
            y: Button.SCALE_DOWN,
            ease: Gsap.Sine.easeInOut,
        });
    }

    protected onPointerUp(pEvent: PIXI.interaction.InteractionEvent): void {
        if (this._eventData !== undefined) {
            Gsap.TweenMax.to(this._visuals.scale, 0.15, {
                x: Button.SCALE_OVER,
                y: Button.SCALE_OVER,
                ease: Gsap.Sine.easeInOut,
            });
            this.playClickedSFX();
        }
        super.onPointerUp(pEvent);
    }

    protected onPointerOut(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerOut(pEvent);
        Gsap.TweenMax.to(this._visuals.scale, 0.15, {
            x: Button.SCALE_NORMAL,
            y: Button.SCALE_NORMAL,
            ease: Gsap.Sine.easeInOut,
        });
    }

    protected playHoverVO(): void {
        if (this._hoverVO !== undefined && this._canPlayVo) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._hoverVO, 1, false, HLF.AudioCategory.VO));
            PubSub.publishSync(Topics.PLAY_CAPTION, new CaptionToken(this._hoverVO));
        }
    }

    protected playClickedSFX(): void {
        if (this._clickedSFX !== undefined) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._clickedSFX, 1, false, HLF.AudioCategory.SFX));
        }
    }
}
